import type { GetDataAnalyticsBrandParams } from "@/lib/api/generated/model";
import { toISODate } from "@rumbledirect/ui-v2/day";
import {
  type EndOfContract,
  toEndOfContractParams,
} from "@/dossiers/endOfContractFilter";

/**
 * Request options every `data-analytics` call must carry. Those 25 endpoints
 * are plain JSON (`formats: ['json']` backend-side — they have no `@id` or
 * `@context`), while the shared API client defaults its `Accept` header to
 * `application/ld+json` for everything else in the app. Without this override
 * every chart request is rejected with 406 Not Acceptable before security is
 * even evaluated. Admin v1 special-cases the same endpoints the same way
 * (`frontend/admin/src/lib/api/resources/analytics.ts`). Do not "tidy" this
 * away, and do not move it into the client's global default: the rest of the
 * app depends on JSON-LD.
 */
export const ANALYTICS_REQUEST_OPTIONS = {
  headers: { Accept: "application/json" },
} as const;

/**
 * One slice of a Reporting breakdown chart: a category and how many dossiers
 * (or media, depending on the endpoint) fall in it.
 */
export interface BreakdownEntry {
  readonly label: string;
  readonly count: number;
}

/**
 * The query parameters every `data-analytics` endpoint shares, aliased from
 * the generated type per admin-v2's data-layer convention: the `start`/`end`
 * range plus the report filters (`dossierType`, `repairType`, `externalInsured`, `claimable`, `brand`
 * and `matchedRule` / `excludeMatchedRule`). The generated per-endpoint params types are structurally
 * identical; one seam type keeps later chunks from depending on 25 aliases.
 */
export type AnalyticsParams = GetDataAnalyticsBrandParams;

/**
 * The report filters as the URL/search layer holds them. The
 * booleans are three-state: `undefined` is "no choice" (no filter sent),
 * `false` a deliberate choice the backend acts on — so `false` must never be
 * collapsed into "unset" (see `dossiers/dossiersSearch.ts`).
 */
export interface AnalyticsFilterSelection {
  readonly externalInsured: boolean | undefined;
  readonly claimable: boolean | undefined;
  readonly brand: readonly string[];
  readonly endOfContract: EndOfContract | undefined;
  readonly dossierType: string | undefined;
  readonly repairType: string | undefined;
}

/** The report-filter subset of {@link AnalyticsParams}. */
export type AnalyticsFilterParams = Pick<
  AnalyticsParams,
  | "externalInsured"
  | "claimable"
  | "dossierType"
  | "repairType"
  | "brand"
  | "matchedRule"
  | "excludeMatchedRule"
>;

/**
 * Turns the chosen report filters into the query parameters every
 * `data-analytics` call on the page carries. Three-state: `false` is sent,
 * only `undefined` omits the parameter — a truthiness check here would
 * silently turn "Intern verzekerd" into "Alle". The brands go as one array
 * under the `brand` key. The end-of-contract selection resolves through the
 * served rule IDs to `matchedRule` (ANY) or `excludeMatchedRule` (NONE).
 * The client serialises these arrays to repeated bracketed keys.
 */
export function toAnalyticsFilterParams(
  selection: AnalyticsFilterSelection,
  ruleIds: readonly string[] = [],
): AnalyticsFilterParams {
  return {
    ...(selection.externalInsured !== undefined
      ? { externalInsured: selection.externalInsured }
      : {}),
    ...(selection.claimable !== undefined
      ? { claimable: selection.claimable }
      : {}),
    ...(selection.dossierType !== undefined
      ? {
          // Validated server-side against the DossierType enum; an unknown value is a 400.
          dossierType: selection.dossierType as AnalyticsParams["dossierType"],
        }
      : {}),
    ...(selection.repairType !== undefined
      ? {
          // Validated server-side against the RepairType enum; an unknown value is a 400.
          repairType: selection.repairType as AnalyticsParams["repairType"],
        }
      : {}),
    ...(selection.brand.length > 0 ? { brand: [...selection.brand] } : {}),
    ...toEndOfContractParams(selection.endOfContract, ruleIds),
  };
}

/**
 * The `ReportingSection` values whose `data-analytics` responses do NOT fit
 * {@link toBreakdownEntries} and have no renderer in the chart registry
 * (`reportingCharts.tsx`): the rule-match list (`RuleMatchItemOutput` rows)
 * and the scalar dossier count (`totalDossiers`, which the KPI row's
 * `dossiers` tile reads via {@link toTotalDossiersCount} instead of a chart).
 * The registry's key type excludes them, so wiring one up means removing it
 * from this guard list first.
 */
export const NON_BREAKDOWN_SECTIONS = [
  "ruleMatch", // list of RuleMatchItemOutput
  "totalDossiers", // scalar totalDossiersToCount, rendered by the KPI row
] as const;

function isNumberRecord(value: unknown): value is Record<string, number> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  return Object.values(value).every((entry) => typeof entry === "number");
}

/**
 * Normalises any uniform `data-analytics` breakdown response into chart-ready
 * entries. Every such response is `{ start, end, <payload>: Record<string, number> }`
 * carrying exactly one breakdown-map property; matching on that value shape
 * rather than on a property-name convention (or a 25-line endpoint-to-property
 * map maintained against the backend) also covers the ones whose name breaks
 * the `*ToCount` pattern (`service-partner-preferred` returns
 * `preferredServicePartnerCount`) and automatically excludes the non-uniform
 * endpoints: a scalar count, a list of rows, and the string `start`/`end`
 * fields are never a `Record<string, number>`.
 *
 * Returns `[]` when the payload does not hold exactly one breakdown map —
 * including an empty map, which matches and maps to no entries — rather than
 * throwing: an empty chart renders its empty state, it does not take the page
 * down.
 */
export function toBreakdownEntries(payload: unknown): BreakdownEntry[] {
  if (payload === null || typeof payload !== "object") {
    return [];
  }

  const maps = Object.values(payload).filter(isNumberRecord);
  if (maps.length !== 1) {
    return [];
  }

  return Object.entries(maps[0]).map(([label, count]) => ({ label, count }));
}

/**
 * Turns a chosen date range into the query params the `data-analytics`
 * endpoints expect. `start` is required; `end` is included up to and including
 * today and omitted only when it would land in the future (mirroring admin
 * v1), because the analytics query has no data for days that have not happened
 * yet. Days, not instants: the API interprets the range server-side.
 */
export function toAnalyticsRange(start: Date, end: Date): AnalyticsParams {
  const params: AnalyticsParams = { start: toISODate(start) };
  // ISO days are zero-padded, so lexicographic comparison is chronological.
  if (toISODate(end) <= toISODate(new Date())) {
    params.end = toISODate(end);
  }
  return params;
}

/**
 * Extracts the scalar count from the `total-dossiers` response
 * (`{ start, end, totalDossiersToCount }`). Returns `undefined` for any other
 * payload so the KPI tile falls back to its empty/loading state rather than
 * rendering a raw object or throwing.
 */
export function toTotalDossiersCount(payload: unknown): number | undefined {
  if (payload === null || typeof payload !== "object") {
    return undefined;
  }
  const count = (payload as { totalDossiersToCount?: unknown })
    .totalDossiersToCount;
  return typeof count === "number" && Number.isFinite(count)
    ? count
    : undefined;
}

/**
 * The signed percentage change from `previous` to `current`, rounded to whole
 * points — the KPI delta on the Reporting page. Returns `null` when no
 * meaningful delta exists: comparison off, either value missing, or
 * `previous` zero (a "+100%" against nothing is noise, and dividing by it is
 * a bug).
 */
export function toPercentageDelta(
  current: number | undefined,
  previous: number | undefined,
): number | null {
  if (current === undefined || previous === undefined || previous <= 0) {
    return null;
  }
  return Math.round(((current - previous) / previous) * 100);
}
